import { memo } from 'react';
import {
  FooterDataDto,
  ManualDefinitionTableDataObject,
  TableColumnConfigDto,
  TableDataDto,
  TableRowDto,
  WidgetDto,
} from '@asset-metrix/portal-gateway-client-js';
import {
  ButtonsCellRenderer,
  CheckboxCellRendererDecorator,
  combineFormatters,
  FileDownloadCellRenderer,
  FileNameCellRenderer,
  IconCellRenderer,
  IIconCellRendererParams,
  MultiValueCellRenderer,
  numberFactorFormatter,
  ProgressBarCellRenderer,
  ProgressBarProps,
  WorkflowStepFile,
  WorkflowVisualizationCellRenderer,
} from '@asset-metrix/portal-ui';
import {
  type CellRendererSelectorFunc,
  CellRendererSelectorResult,
  GROUP_AUTO_COLUMN_ID,
  type ICellRendererParams,
} from 'ag-grid-community';

import { portalGatewayClient } from '../../api/portalGatewayClient';
import { SigningRequest } from '../../store/states/documentSigning';
import { Filter } from '../../store/states/filterTypes';
import { invalidateTable } from '../../utils/queryCacheHelper';
import { triggerDownloadWithAnchorTag } from '../../utils/triggerDownloadWithAnchorTag';

import { formattersByType } from './configToColDef';
import { DetailPageCellRendererContainer } from './DetailPageCellRendererContainer';
import { extractValue } from './extractValue';
import {
  FooterCellRenderer,
  FooterCellRendererProps,
} from './FooterCellRenderer';
import {
  getDetailPaneCellRendererDecorator,
  isTableColumnConfigWithDetailPane,
} from './getDetailPaneCellRendererDecorator';
import { isPageFilterColumnConfig } from './usePageFilterColumnState';

export type CellRendererSelectorOptions = {
  dataConfigs?: TableDataDto['configs'];
  deepestGroupLevel?: number;
  isFullScreenMode?: boolean;
  isGroupedTable?: boolean;
  isSomeTopLevelGroupExpanded?: boolean;
  pageFilter?: Filter;
  showFullTextSearchResults?: boolean;
  workflowActionHandler?: (
    modalId: string,
    rowIds: string[],
    action: string,
    data: TableRowDto['data'][],
    documentId?: string
  ) => void;
  footerData?: FooterDataDto;
  footerTitleColumnKey?: string;
  currentSigningRequests?: SigningRequest[];
  isSigningInProgress?: boolean;
  setFilterFromPageFilterColumn?: (
    event: React.ChangeEvent<HTMLInputElement>,
    params: ICellRendererParams<TableRowDto['data']>
  ) => void;
  isPageFilterColumnChecked?: (
    params: ICellRendererParams<TableRowDto['data']>
  ) => boolean;
  rowIdToHighlight?: string;
};

function getDefaultCellRenderer(
  config: TableColumnConfigDto,
  params: ICellRendererParams<TableRowDto['data']>,
  widgetId: WidgetDto['id'],
  {
    currentSigningRequests,
    dataConfigs,
    isSigningInProgress,
    pageFilter,
    showFullTextSearchResults,
    workflowActionHandler,
  }: CellRendererSelectorOptions = {}
): CellRendererSelectorResult | undefined {
  if (config.type === 'file_name') {
    return {
      component: memo(FileNameCellRenderer),
      params: {
        onFileDownload: async (
          fileId: string,
          shouldRefetchTableData = false
        ) => {
          try {
            const { data } =
              await portalGatewayClient.table.getDocumentDownloadUrl(
                fileId,
                widgetId
              );
            if (data.status === 'success' && data.data.url) {
              triggerDownloadWithAnchorTag(data.data.url);
              shouldRefetchTableData &&
                (await invalidateTable({ dataSourceType: 'docview' }));
            }
          } catch (e: unknown) {
            console.error(e);
            throw e;
          }
        },
        showFullTextSearchResults,
        showUnreadIndicator: config.showUnreadIndicator,
      },
    };
  } else if (config.type === 'fileDownload') {
    return {
      component: memo(FileDownloadCellRenderer),
      params: {
        onFileClick: async (file: WorkflowStepFile) => {
          try {
            const { data } =
              await portalGatewayClient.table.getDocumentDownloadUrl(
                file.id,
                widgetId,
                { token: file.downloadToken ?? undefined }
              );
            if (data.status === 'success' && data.data.url) {
              triggerDownloadWithAnchorTag(data.data.url);
            }
          } catch (e: unknown) {
            console.error(e);
            throw e;
          }
        },
      },
    };
  } else if (config.type === 'link') {
    return {
      component: memo(DetailPageCellRendererContainer),
      params: {
        detailPageConfig: getDetailPageConfig(config, params, dataConfigs),
        pageFilter,
      },
    };
  } else if (config.type === 'workflowActions') {
    // workflows active
    if (params.value?.workflowStatus?.state === 'active') {
      return {
        component: memo(ButtonsCellRenderer),
        params: {
          buttons: Array.isArray(params.value.actions)
            ? params.value.actions.map(
                (action: ManualDefinitionTableDataObject) => ({
                  children: action.label,
                  loading: currentSigningRequests?.some(
                    (r) => r.documentId === action.documentId
                  ),
                  disabled: isSigningInProgress,
                  onClick: () => {
                    if (
                      workflowActionHandler != null &&
                      action.key != null &&
                      typeof action.key === 'string' &&
                      typeof action.modalId === 'string' &&
                      params.data !== undefined
                    ) {
                      workflowActionHandler(
                        action.modalId,
                        [String(params.value.rowId)],
                        action.key,
                        [params.data],
                        typeof action.documentId === 'string'
                          ? action.documentId
                          : undefined
                      );
                    }
                  },
                  variant: ['cancel', 'reject', 'close'].some((v) =>
                    String(action.label).toLowerCase().includes(v)
                  )
                    ? 'text'
                    : 'primary',
                })
              )
            : [],
        },
      };
    }

    // workflows locked
    return {
      component: memo(IconCellRenderer),
      params: {
        value: {
          name: 'lock',
          disabled: true,
          tooltip: params.value?.workflowStatus?.stateHint,
        },
      } as IIconCellRendererParams,
    };
  } else if (config.type === 'workflowProgress') {
    const progressBar: ProgressBarProps = {
      segments: Array.isArray(params.value)
        ? params.value.map((v, idx) => {
            return {
              ...v,
              value: numberFactorFormatter(v.value, config) ?? v.value,
              valueFormatted: combineFormatters(
                formattersByType['number'],
                config
              )(v),
            };
          })
        : [],
      unit: '%',
    };

    return {
      component: memo(ProgressBarCellRenderer),
      params: {
        progressBar,
      },
    };
  } else if (config.type === 'workflowVisualization') {
    return {
      component: memo(WorkflowVisualizationCellRenderer),
      params: {
        onFileClick: async (file: WorkflowStepFile) => {
          try {
            const { data } =
              await portalGatewayClient.table.getDocumentDownloadUrl(
                file.id,
                widgetId,
                { token: file.downloadToken ?? undefined }
              );
            if (data.status === 'success' && data.data.url) {
              triggerDownloadWithAnchorTag(data.data.url);
            }
          } catch (e: unknown) {
            console.error(e);
            throw e;
          }
        },
      },
    };
  } else if (params.value == null) {
    // Maybe ag-grid failed to extract the correct value
    const extractedValue = extractValue(config.key, params.data);

    if (Array.isArray(extractedValue)) {
      return {
        component: memo(MultiValueCellRenderer),
        params: {
          value: extractedValue,
        },
      };
    }
  }
}

function getDetailPageConfig(
  config: TableColumnConfigDto,
  params: ICellRendererParams,
  dataConfigs: CellRendererSelectorOptions['dataConfigs']
) {
  const detailPages =
    (params.data?.originalRowIndex != null &&
      dataConfigs?.[params.data.originalRowIndex]?.detailPages) ||
    [];
  const detailPage = detailPages.find(
    (dp) => dp.datacolumnKey === params.column?.getColId()
  );
  return config.detailPageConfig?.find(
    (dpc) => dpc.id === detailPage?.detailPageId
  );
}

function getFooterParams(
  columnConfig: TableColumnConfigDto,
  params: ICellRendererParams,
  isFooterLoading: boolean,
  footerTitleColumnKey?: string
): FooterCellRendererProps {
  if (footerTitleColumnKey === params.column?.getColId()) {
    return {
      isTitle: true,
      titleValue: params.value,
    };
  }

  if (!columnConfig.appearsInFooter) {
    return {
      isTitle: false,
      isEnabled: false,
    };
  }

  return {
    isEnabled: true,
    isTitle: false,
    isLoading: isFooterLoading,
    valueFormatted: params.valueFormatted ?? '',
  };
}

function getCheckboxCellRendererDecoratorParams(
  params: ICellRendererParams,
  cellRendererConfig?: CellRendererSelectorResult,
  options?: CellRendererSelectorOptions
) {
  return {
    cellRendererConfig,
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
      options?.setFilterFromPageFilterColumn?.(event, params);
    },
    isChecked: options?.isPageFilterColumnChecked?.(params) ?? false,
  };
}

function applyCellRendererDecorators(
  tableColumnConfigDto: TableColumnConfigDto,
  columnsConfig: TableColumnConfigDto[],
  params: ICellRendererParams,
  cellRendererConfig?: CellRendererSelectorResult,
  options?: CellRendererSelectorOptions
) {
  if (
    isPageFilterColumnConfig(tableColumnConfigDto) &&
    options?.isFullScreenMode !== true
  ) {
    return {
      component: memo(CheckboxCellRendererDecorator),
      params: getCheckboxCellRendererDecoratorParams(
        params,
        cellRendererConfig,
        options
      ),
    };
  }
  if (isTableColumnConfigWithDetailPane(tableColumnConfigDto)) {
    const detailPaneConfigId =
      params.data?.originalRowIndex != null &&
      options?.dataConfigs?.[params.data.originalRowIndex]?.detailPaneId;

    if (detailPaneConfigId) {
      return getDetailPaneCellRendererDecorator(
        detailPaneConfigId,
        params,
        tableColumnConfigDto,
        columnsConfig,
        cellRendererConfig
      );
    }
  }
  return cellRendererConfig;
}

export function getCellRendererSelector(
  tableColumnConfigDto: TableColumnConfigDto,
  widgetId: WidgetDto['id'],
  isFooterLoading: boolean,
  columnsConfig: TableColumnConfigDto[],
  options?: CellRendererSelectorOptions
): CellRendererSelectorFunc {
  return (params) => {
    // do not apply custom cell renderers for auto columns like grouping columns etc.
    if (params.column?.getColId() === GROUP_AUTO_COLUMN_ID) {
      return undefined;
    }

    if (params.node.isRowPinned()) {
      return {
        component: FooterCellRenderer,
        params: getFooterParams(
          tableColumnConfigDto,
          params,
          isFooterLoading,
          options?.footerTitleColumnKey
        ),
      };
    }
    const cellRendererConfig = getDefaultCellRenderer(
      tableColumnConfigDto,
      params,
      widgetId,
      options
    );

    return applyCellRendererDecorators(
      tableColumnConfigDto,
      columnsConfig,
      params,
      cellRendererConfig,
      options
    );
  };
}
